import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Alert, Button, Space, Tabs, Tag, Tooltip, Typography } from 'antd'
import { ArrowLeftOutlined, FilterOutlined, SyncOutlined } from '@ant-design/icons'
import FeatureThemeProvider from '@/components/FeatureThemeProvider'
import { useAuthSession } from '@/lib/session'
import { UserTypes } from '@/types/authorization'
import type { McpAccessPerson } from '@/types/mcp'
import ConnectionsTable from '../components/ConnectionsTable'
import McpManageGate from '../components/McpManageGate'
import { useCanManageMcp, useMcpAccess, useMcpConnections, useSupportMcpConnections } from '../hooks'
import { useDisconnectConnection } from '../useDisconnectConnection'
import PeopleTab from './PeopleTab'

const TAB_KEYS = ['people', 'connections', 'support'] as const
type TabKey = (typeof TAB_KEYS)[number]

function isTabKey(key: string): key is TabKey {
  return (TAB_KEYS as readonly string[]).includes(key)
}
type UserFilter = Pick<McpAccessPerson, 'userId' | 'name'>

function ClientConnectionsTab({
  clientId,
  userFilter,
  onClearUserFilter,
}: {
  clientId: string
  userFilter: UserFilter | null
  onClearUserFilter: () => void
}) {
  const connections = useMcpConnections(clientId)
  const { confirmDisconnect, disconnectingId } = useDisconnectConnection()
  const all = connections.data ?? []
  const rows = userFilter ? all.filter((connection) => connection.userId === userFilter.userId) : all

  return (
    <div>
      <Space wrap className="mb-4">
        {userFilter && (
          <Tag color="cyan" closable onClose={onClearUserFilter} icon={<FilterOutlined />}>
            User: {userFilter.name}
          </Tag>
        )}
        <Tooltip title="Refresh table">
          <Button icon={<SyncOutlined />} aria-label="Refresh table" onClick={() => void connections.refetch()} />
        </Tooltip>
      </Space>
      {connections.isError && (
        <Alert
          type="error"
          showIcon
          className="mb-4"
          message={connections.error instanceof Error ? connections.error.message : 'Failed to load connections'}
        />
      )}
      <ConnectionsTable
        connections={rows}
        loading={connections.isLoading || connections.isFetching}
        emptyText={
          connections.isError
            ? 'Couldn’t load connections.'
            : userFilter
              ? `${userFilter.name} has no AI assistant connections.`
              : 'No AI assistant connections in the last 30 days.'
        }
        disconnectingId={disconnectingId}
        onDisconnect={(connection) =>
          confirmDisconnect(connection, {
            title: `Disconnect ${connection.appName || 'this app'} for ${connection.userName}?`,
            content: 'They can connect again while they still have access.',
            success: `Disconnected ${connection.appName || 'the app'} for ${connection.userName}`,
          })
        }
      />
      <p className="mt-2 text-xs text-gray-500">
        Live connections first, then ones that ended in the last 30 days.
      </p>
    </div>
  )
}

function SupportConnectionsTab() {
  const connections = useSupportMcpConnections(true)
  const { confirmDisconnect, disconnectingId } = useDisconnectConnection()

  return (
    <div>
      <p className="mb-4 text-sm text-gray-500">
        DI admins&rsquo; connections to the AdvicePrep support server. They aren&rsquo;t tied to the client above.
      </p>
      {connections.isError && (
        <Alert
          type="error"
          showIcon
          className="mb-4"
          message={connections.error instanceof Error ? connections.error.message : 'Failed to load connections'}
        />
      )}
      <ConnectionsTable
        connections={connections.data ?? []}
        loading={connections.isLoading || connections.isFetching}
        emptyText={connections.isError ? 'Couldn’t load connections.' : 'No support server connections in the last 30 days.'}
        disconnectingId={disconnectingId}
        onDisconnect={(connection) =>
          confirmDisconnect(connection, {
            title: `Disconnect ${connection.appName || 'this app'} for ${connection.userName}?`,
            content: 'They can connect again while they still have access.',
            success: `Disconnected ${connection.appName || 'the app'} for ${connection.userName}`,
          })
        }
      />
    </div>
  )
}

export default function McpAccessPage() {
  const navigate = useNavigate()
  const session = useAuthSession()
  const clientId = session?.currentClientId ?? ''
  const clientName = session?.currentClientName || 'this client'
  // Owners share the DI admin role list, so the permission alone can't tell them apart.
  const isDiAdmin = session?.currentUserTypeId === UserTypes.Admin
  const { canManage } = useCanManageMcp(clientId)
  const access = useMcpAccess(clientId, canManage)
  const [tab, setTab] = useState<TabKey>('people')
  const [userFilter, setUserFilter] = useState<UserFilter | null>(null)

  useEffect(() => {
    setUserFilter(null)
  }, [clientId])

  const withAccess = access.data?.filter((person) => person.hasAccess).length

  const items = [
    {
      key: 'people',
      label: 'People',
      children: (
        <PeopleTab
          key={clientId}
          clientId={clientId}
          clientName={clientName}
          onShowConnections={(person) => {
            setUserFilter({ userId: person.userId, name: person.name })
            setTab('connections')
          }}
        />
      ),
    },
    {
      key: 'connections',
      label: 'Connections',
      children: (
        <ClientConnectionsTab
          clientId={clientId}
          userFilter={userFilter}
          onClearUserFilter={() => setUserFilter(null)}
        />
      ),
    },
    ...(isDiAdmin ? [{ key: 'support', label: 'Support (DI)', children: <SupportConnectionsTab /> }] : []),
  ]

  return (
    <FeatureThemeProvider featureKey="pracIntel" withAntd className="px-6 py-6 md:px-8">
      <div className="relative mx-auto w-full max-w-6xl">
        <div className="pt-1 pb-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <Typography.Title level={3} className="!mb-1">
                  MCP access
                </Typography.Title>
                {withAccess !== undefined && <Tag color="cyan-inverse">{withAccess} with access</Tag>}
              </div>
              <Typography.Paragraph type="secondary" className="!mb-0 max-w-4xl">
                People with MCP access can connect an AI assistant to {clientName} and use your DashboardInsights products from it,
                only as far as they can here. It can make a few changes, such as adding findings or marking
                assets reviewed, and is told to check with the person first. It can&rsquo;t send emails to clients,
                delete anything, or start paid analysis.
              </Typography.Paragraph>
            </div>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/reports/dashboard-builder')}>
              Back to PracIntel
            </Button>
          </div>
        </div>
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          {clientId ? (
            <McpManageGate clientId={clientId} deniedMessage={`You can’t manage MCP access for ${clientName}.`}>
              <Tabs activeKey={tab} onChange={(key) => isTabKey(key) && setTab(key)} items={items} />
            </McpManageGate>
          ) : (
            <Alert type="info" showIcon message="Choose a client to manage who can use an AI assistant with it." />
          )}
        </div>
      </div>
    </FeatureThemeProvider>
  )
}
